import React from 'react';
import { checkNumbersArr } from './imeiValidator';
import * as turf from '@turf/turf';
import cloneDeep from 'lodash/cloneDeep';

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const validateImei = (aTestValue) => {
  if (!validateNumbers(aTestValue)) return false;
  const imeiNumber = Array.from(aTestValue, (x) => Number(x));
  return checkNumbersArr(imeiNumber);
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const validateId = (aTestValue) => {
  if (!aTestValue || (aTestValue.length <= 2 && aTestValue.length >= 65)) return false;
  const re = /^[a-zA-Z0-9-_]+$/;
  return re.test(aTestValue);
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const validateNameOrId = (aTestValue) => {
  const re = /^[a-zA-Z0-9-]+$/;
  return re.test(aTestValue);
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
const validateOrgIdRegExp = (aTestValue) => {
  const re = /^[a-zA-Z0-9-_ ]+$/;
  return re.test(aTestValue);
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const validateString = (aTestValue) => {
  if (!aTestValue || (aTestValue.length <= 2 && aTestValue.length >= 65)) return false;
  const re = /^[a-zA-Z0-9- ]+$/;
  return re.test(aTestValue);
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const validateNumbers = (aTestValue, required = false) => {
  if (!required && !aTestValue) return true;
  const re = /^[0-9]+$/;
  return re.test(aTestValue);
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const validateUserId = (value, required = false) => {
  if (!required && !value) return true;
  return !!value && value.length > 2 && value.length < 61 && validateNameOrId(value);
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
// Validate 15 digits IMEI
export const validateAssetId = (value, required = false) => {
  if (!required && !value) return true;
  return !!value && value.length === 15 && validateNumbers(value, required);
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const validateEmail = (email, required = false) => {
  if (!required && !email) return true;
  if (!email || !email.length) return false;
  const re =
    /^([a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?)$/;
  return re.test(email);
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const validateOrgId = (value, required = true) => {
  if (!required && !value) return true;
  return !!value && !!value.length && value.length > 2 && value.length < 61 && validateOrgIdRegExp(value);
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const validateName = (value, required = true) => {
  if (!required && !value) return true;
  return !!value && !!value.length && value.length > 2 && value.length < 61 && validateNameOrId(value);
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const validatePlanId = (value) => !!value && !!value.length && value.length > 2 && value.length < 61 && validateNameOrId(value);

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const validateApiKeyId = (aValue) => !!aValue && aValue.length > 2 && aValue.length < 61 && validateNameOrId(aValue);

const validateExpiry = (aValue) => {
  if (!aValue) return false;

  switch (typeof aValue) {
    case 'string':
      const inDate = new Date(aValue);
      const now = new Date();
      return Boolean(inDate > now);
    case 'number':
      return Boolean(aValue > Date.now());
      break;
    default:
      console.log('validateExpiry unhandeled type', aValue, typeof aValue);
  }
  return true;
  //  !!aValue && ((typeof aValue === 'number' && aValue > Date.now()) || (typeof aValue === 'string' && Date(aValue) > Date()));
};
// const validateCreated = (aValue) => !!aValue && typeof aValue === 'number';
// const validateState = (aValue) => ['Active', 'Revoked'].includes(aValue);

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const validatePhoneNumber = (value, required = false) => {
  if (!required && !value) return true;
  return !!value && value.length === 10 && /^\d+$/.test(value);
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const validatePassword = (value, required = false) => {
  if (!required && !value) return true;
  return !!value && value.length > 6;
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const validateUserRole = (roles) => {
  if (!roles || !roles.length) return false;
  if (roles.includes('Installer')) {
    return roles.length === 1;
  }
  return ['User', 'Admin', 'SuperAdmin', 'Manager'].some((r) => roles.includes(r));
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
export function validateAlert({ values, entityName }) {
  return {};
}

// /////////////////////////////////////////////////////////////////////////////////////////////////
export function validateFeature({ values, entityName }) {
  return {
    entityId: !validateName(values.entityId, true) ? 'Feature Key is required' : '',
  };
}

// /////////////////////////////////////////////////////////////////////////////////////////////////
export function validateFeatureOverride({ values, entityName }) {
  return {
    featureId: !validateName(values.featureId, true) ? 'Feature Override Key is required' : '',
    orgId: !validateOrgId(values.orgId) ? `${entityName} organization Id is required` : '',
  };
}

// /////////////////////////////////////////////////////////////////////////////////////////////////
export function validateUser({ values, entityName }) {
  return {
    // The entityId is the email address, so checking for EMail validity instead!!!
    entityId: !validateEmail(values.entityId, true) ? 'User Id is required' : '',
    email: !validateEmail(values?.email, true) ? 'Incorrect Email Format' : '',
    firstName: values?.firstName && !validateUserId(values?.firstName) ? 'User First Name is required' : '',
    lastName: values?.lastName && !validateUserId(values?.lastName) ? 'User Last Name is required' : '',
    role: !validateUserRole(values?.userRoles) ? 'User Role must be one of [User, Admin, SuperAdmin]' : '',
  };
}

// /////////////////////////////////////////////////////////////////////////////////////////////////
export function validateDriver({ values, entityName }) {
  return {
    email: !validateEmail(values?.email, true) ? 'Incorrect Email Format' : '',
    firstName: values?.firstName && !validateUserId(values?.firstName) ? 'Driver First Name is required' : '',
    lastName: values?.lastName && !validateUserId(values?.lastName) ? 'Driver Last Name is required' : '',
  };
}

// /////////////////////////////////////////////////////////////////////////////////////////////////
export function validateOrg({ values, entityName }) {
  return {
    entityId: !validateOrgId(values.entityId) ? `${entityName} organization Id is required` : '',
    entityName: !validateOrgId(values.entityName) ? `${entityName} name is required` : '',
    email: values?.email && !validateEmail(values?.email) ? 'Incorrect e-mail format' : '',
    phoneNumber: values?.phoneNumber && !validatePhoneNumber(values?.phoneNumber) ? 'Incorrect phone number format' : '',
  };
}

// /////////////////////////////////////////////////////////////////////////////////////////////////
export function validateOrgCreate({ values, entityName }) {
  return {
    entityName: !validateOrgId(values.entityName) ? `${entityName} name is required` : '',
    email: values?.email && !validateEmail(values?.email) ? 'Incorrect e-mail format' : '',
    phoneNumber: values?.phoneNumber && !validatePhoneNumber(values?.phoneNumber) ? 'Incorrect phone number format' : '',
  };
}

// /////////////////////////////////////////////////////////////////////////////////////////////////
export function validateTrip({ values, entityName }) {
  return {
    entityId: !validateOrgId(values.entityId) ? `${entityName} Id is required` : '',
    assignedDriverId: !validateId(values.assignedDriverId) ? `Driver name must be selected ${JSON.stringify(values)}` : '',
    assignDefault: values.assignDefault && !values.assignedDriverId ? `Can't Assign Driver to Trips without selecting a valid driver` : '',
  };
}

// /////////////////////////////////////////////////////////////////////////////////////////////////
export function validatePlan({ values, entityName }) {
  return {
    entityName: !validateOrgId(values.entityName) ? `${entityName} name is required` : '',
    planType: !validateOrgId(values?.planType) ? `${entityName} type is required` : '',
  };
}

// /////////////////////////////////////////////////////////////////////////////////////////////////
export function validateApiKey({ values, entityName }) {
  return {
    entityName: !validateApiKeyId(values.entityName) ? 'Invalid API Key: The API Key should not contain special characters or spaces' : '',
    roles: !validateUserRole(values.roles) ? 'Role must be one of [User, Admin, SuperAdmin]' : '',
    expiry: !validateExpiry(values.expiry) ? 'Expiry must be in the future' : '',
  };
}

// /////////////////////////////////////////////////////////////////////////////////////////////////
export function validateAsset({ values, entityName }) {
  return {
    entityId: !validateAssetId(values.entityId, true) ? `15 Digits ${entityName} Id is required` : '',
  };
}

// /////////////////////////////////////////////////////////////////////////////////////////////////
export function validateCommand({ values, entityName }) {
  return {
    assetId: !validateAssetId(values.assetId, true) ? `15 Digits Asset Id is required` : '',
    commandText: !values.commandText || values.commandText.length === 0 ? `Command Text is required` : '',
  };
}

// /////////////////////////////////////////////////////////////////////////////////////////////////
export function validateSafetyEvent({ values, entityName }) {
  return {
    id: !values.id ? `${entityName} Id is required` : '',
  };
}

// /////////////////////////////////////////////////////////////////////////////////////////////////
export function validateTag({ values, entityName }) {
  return {
    tagValue: !validateString(values.tagValue) ? 'Invalid or missing tag value' : '',
  };
}

// /////////////////////////////////////////////////////////////////////////////////////////////////
export function validateNone() {
  return { error: 'ERROR: Trying to validated an unknown entity' };
}

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const validateUrl = (url) => {
  try {
    url = new URL(url);
  } catch (e) {
    return false;
  }
  return url.protocol === 'http:' || url.protocol === 'https:';
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const capitalize = (str) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
// Utility functions for react-table
// /////////////////////////////////////////////////////////////////////////////////////////////////
export const Q_DEF_RETRY = 1;
export const Q_HTTP_TIMEOUT = 60_000; // Default wait timeout for http queries - 60 seconds
export const Q_STALE_TMO_CFG = 10_000; // Stale timeout for config items (should be short) - 10 seconds for now
export const Q_STALE_TMO_STS = 90_000; // Stale timeout for status/statistics items (should be long) - 1.5 minutes
export const Q_LONG_POLL_TMO = 5 * 60 * 1000; // Long poll timeout for useGetAssets etc - 5 minutes

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const onSettledT = (queryClient, keys) => {
  queryClient.invalidateQueries({ queryKey: keys });
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const onMutateCreateT = (queryClient, keys, newInfo) => {
  queryClient.setQueryData(keys, (prevs) => {
    // Callback with current cached data
    if (!prevs) return [{ ...newInfo }]; // If no previous data, create new array
    if (Array.isArray(prevs)) {
      return [...prevs, { ...newInfo }];
    } else {
      return { count: prevs.count + 1, offset: prevs.offset, rows: [...prevs.rows, { ...newInfo }] };
    }
  });
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const onMutateUpdateT = (queryClient, keys, newInfo, fieldName) => {
  queryClient.setQueryData(keys, (prevs) => {
    if (!prevs) return prevs;
    if (Array.isArray(prevs)) {
      return prevs.map((prevInfo) => (prevInfo[fieldName] === newInfo[fieldName] ? newInfo : prevInfo));
    } else {
      const rows = prevs.rows.map((prevInfo) => (prevInfo[fieldName] === newInfo[fieldName] ? newInfo : prevInfo));
      return { count: prevs.count, offset: prevs.offset, rows };
    }
  });
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
function countKeys(obj) {
  if (typeof obj !== 'object' || obj === null) return 0;
  const keys = Object.keys(obj);
  let sum = keys.length;
  keys.forEach((key) => (sum += countKeys(obj[key])));
  return sum;
}

// /////////////////////////////////////////////////////////////////////////////////////////////////
export async function mutationFnUpdateT({ values, origValues, updateCB, entityType }) {
  let auxValues = cloneDeep(values);
  const ignoreDiff = ['appAlerts'];
  // Add config fields that should be sent whole, not diffed.
  // Later add ('deviceConfig', 'safetyConfig', 'firmwareConfig', 'odomaticConfig', 'deviceExclConfig')
  const sendWhole = ['adminConfig'];

  // Igonoring the difference checking for appAlerts due to alerts stored in jsonB so that we can update the alert whole object.
  if (!ignoreDiff.includes(entityType)) auxValues = getUpdatedDiff(origValues, values);

  // Restore full config objects that should be replaced entirely, not diffed
  for (const key of sendWhole) {
    if (values[key] !== undefined) auxValues[key] = values[key];
  }

  if (countKeys(auxValues) > 1) {
    await updateCB({ ...auxValues, entityType }).catch((err) => {
      console.warn(`mutationFnUpdateT ${entityType}:`, err);
      return Promise.reject(err);
    });
  } else {
    console.log(`mutationFnUpdateT ${entityType} nothing to update`);
  }
}

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const onMutateDeleteT = (queryClient, keys, id, fieldName) => {
  queryClient.setQueryData(keys, (prevs) => {
    if (Array.isArray(prevs)) {
      return prevs.filter((prev) => prev[fieldName] !== id);
    } else {
      const rows = prevs.rows.filter((prev) => prev[fieldName] !== id);
      return { count: prevs.count, offset: prevs.offset, rows };
    }
  });
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
export function removeNestedUndefined(obj) {
  for (const key in obj) {
    if (obj[key] === undefined) {
      delete obj[key];
    } else if (typeof obj[key] === 'object') {
      removeNestedUndefined(obj[key]);
    }
  }
}

// /////////////////////////////////////////////////////////////////////////////////////////////////
const isObj = (obj) => obj && typeof obj === 'object' && !Array.isArray(obj);

// /////////////////////////////////////////////////////////////////////////////////////////////////
function keepDiff(elementDest, elementSrc, ignoreArr) {
  for (const key in elementDest) {
    if (ignoreArr.includes(key)) continue;
    const dstVal = elementDest[key];
    if (dstVal === undefined) {
      delete elementDest[key];
      continue;
    }
    const srcVal = elementSrc[key];
    if (isObj(dstVal) && isObj(srcVal)) {
      keepDiff(dstVal, srcVal, ignoreArr);
      if (Object.keys(dstVal).length === 0) delete elementDest[key];
    } else {
      if (dstVal !== srcVal) continue;
      if (dstVal === srcVal) delete elementDest[key];
    }
  }
}

// /////////////////////////////////////////////////////////////////////////////////////////////////
function getUpdatedDiff(orig, updated) {
  const toUpdate = cloneDeep(updated);
  keepDiff(toUpdate, orig, ['id', 'entityId', 'entityType', 'parentOrgId']);
  return toUpdate;
}

// /////////////////////////////////////////////////////////////////////////////////////////////////
const flattenObject = (obj) => {
  const flattened = {};
  Object.keys(obj).forEach((key) => {
    const value = obj[key];
    if (isObj(value)) {
      Object.assign(flattened, flattenObject(value));
    } else {
      flattened[key] = value;
    }
  });
  return flattened;
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
const flattenObjectCond = (obj, lvl) => {
  const flattened = {};
  Object.keys(obj).forEach((key) => {
    const value = obj[key];
    if (isObj(value) && lvl > 0) {
      Object.assign(flattened, flattenObjectCond(value, lvl - 1));
    } else {
      flattened[key] = value;
    }
  });
  return flattened;
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const flattenTopLevel = (obj) => {
  return flattenObjectCond(obj, 1);
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const debounce = (func, delay) => {
  let timer;
  return function (...args) {
    clearTimeout(timer); // Clears the timer if the function is called again before the delay
    timer = setTimeout(() => {
      func.apply(this, args); // Calls the function after the delay
    }, delay);
  };
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const addCenter = (type, geometryData) => {
  let geoJson;
  if (type === 'rectangle') {
    geoJson = {
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: [
          [
            [geometryData.west, geometryData.north],
            [geometryData.east, geometryData.north],
            [geometryData.east, geometryData.south],
            [geometryData.west, geometryData.south],
            [geometryData.west, geometryData.north],
          ],
        ],
      },
    };
  } else {
    geoJson = {
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: [geometryData.map((coord) => [coord.lng, coord.lat])],
      },
    };
  }

  const centroid = turf.centroid(geoJson);

  return {
    lat: centroid.geometry.coordinates[1],
    lng: centroid.geometry.coordinates[0],
  };
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const convertTime = (msec) => {
  if (!msec) return '00:00:00';
  // Calculate the number of seconds
  let seconds = Math.floor(msec / 1000);

  // Calculate the hours, minutes, and remaining seconds
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds - hours * 3600) / 60);
  seconds = seconds - minutes * 60 - hours * 3600;

  // Ensure that hours, minutes, and seconds are two digits
  const formattedHours = String(hours).padStart(2, '0');
  const formattedMinutes = String(minutes).padStart(2, '0');
  const formattedSeconds = String(seconds).padStart(2, '0');

  // Return the formatted time
  return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
};

// ////////////////////////////////////////////////////////////////////////////////////////////////
export const calWidthAndLengthRecantangle = (geometry) => {
  const { west, east, north, south } = geometry;

  // Calculate the width (horizontal distance) from west to east at the northern latitude
  const width = turf.distance([west, north], [east, north], { units: 'meters' });

  // Calculate the length (vertical distance) from north to south at the western longitude
  const length = turf.distance([west, north], [west, south], { units: 'meters' });

  // Calculate the area by multiplying width and length
  const area = width * length;

  return { width, length, area };
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const milesToKm = (miles) => {
  if (miles) {
    if (typeof miles === 'string') miles = parseFloat(miles);
    return Number((miles * 1.60934).toFixed(1));
  }
  return 0;
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const kmToMiles = (km) => {
  if (km) {
    if (typeof km === 'string') km = parseFloat(km);
    return Number((km * 0.621371).toFixed(1));
  }
  return 0;
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
export function safeStringify(obj) {
  const seen = new WeakSet();
  const str = JSON.stringify(obj, (key, value) => {
    if (typeof value === 'object' && value !== null) {
      if (seen.has(value)) {
        // Circular reference found, discard key
        return '[Circular]';
      }
      // Store value in our collection
      seen.add(value);
    }
    return value;
  });
  return str;
}

export function hasValueChanged(obj1, obj2) {
  if (typeof obj1 !== typeof obj2) return true;

  if (typeof obj1 !== 'object' || obj1 === null || obj2 === null) {
    return obj1 !== obj2;
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  // Check if keys differ in count or name
  if (keys1.length !== keys2.length || !keys1.every((k) => keys2.includes(k))) {
    return true;
  }

  // Recursively check each key
  for (const key of keys1) {
    if (hasValueChanged(obj1[key], obj2[key])) {
      return true;
    }
  }

  return false;
}

export const hasEditCredentials = (roles = []) => ['Admin', 'SuperAdmin', 'Manager'].some((role) => roles.includes(role));

export function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function formatDateToLongForm(isoDateString) {
  const date = new Date(isoDateString);

  const options = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };

  return date.toLocaleDateString('en-US', options);
}
